import { useEffect, useRef, useState } from 'react'
import { useParams, useSearch } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { Page } from '@/components/Page'
import { canCreateCommunityContent } from '@/product/community/access'
import { type AttachmentInput } from '@/product/community/shared/Attachments'
import { getTopicSort, pageSize, type Search } from '@/product/community/category/routeUtils'
import { TopicDialog } from '@/product/community/shared/TopicDialog'

import { Toolbar } from './Toolbar'
import { TopicList } from './TopicList'

type CategoryData = RouterOutput['product']['community']['categories']['getCategory']
type TopicListItem = RouterOutput['product']['community']['topics']['listTopics']['topics'][number]

export const Category = observer(() => {
    const { t } = useTranslation()
    const { categoryId } = useParams({ from: '/product/community/categories/$categoryId' })
    const navigate = useNavigate()
    const listSearch = useSearch({ from: '/product/community/categories/$categoryId' })
    const submittedSearchTerm = listSearch.searchTerm || ''
    const [search, setSearch] = useState(submittedSearchTerm)
    const [category, setCategory] = useState<CategoryData | null>(null)
    const [topics, setTopics] = useState<TopicListItem[] | null>(null)
    const [topicCount, setTopicCount] = useState<number | null>(null)
    const [topicDialogOpen, setTopicDialogOpen] = useState(false)
    const requestVersionRef = useRef(0)
    const markedCategoryReadRef = useRef('')
    const canCreateTopic = canCreateCommunityContent()

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion

        setCategory(null)
        setTopics(null)
        setTopicCount(null)

        void Promise.all([
            client.product.community.categories.getCategory.query({ categoryId }, { signal: abortController.signal }),
            client.product.community.topics.listTopics.query(
                {
                    categoryId,
                    limit: pageSize,
                    offset: (listSearch.page - 1) * pageSize,
                    returnCount: true,
                    searchTerm: submittedSearchTerm || undefined,
                    sort: getTopicSort(listSearch.sort),
                },
                { signal: abortController.signal }
            ),
        ])
            .then(([categoryResult, topicsResult]) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setCategory(categoryResult)
                setTopics(topicsResult.topics)
                setTopicCount(topicsResult.count ?? topicsResult.topics.length)
            })
            .catch(() => {
                if (!abortController.signal.aborted && requestVersionRef.current === requestVersion) {
                    setTopics([])
                    setTopicCount(0)
                }
            })

        return () => abortController.abort()
    }, [categoryId, listSearch.page, listSearch.sort, submittedSearchTerm])

    useEffect(() => {
        if (!category || !topics || markedCategoryReadRef.current === categoryId) {
            return
        }

        markedCategoryReadRef.current = categoryId

        void client.product.community.categories.markCategoryRead.mutate({ categoryId }).catch(() => {
            if (markedCategoryReadRef.current === categoryId) {
                markedCategoryReadRef.current = ''
            }
        })
    }, [category, categoryId, topics])

    useEffect(() => {
        setSearch(submittedSearchTerm)
    }, [submittedSearchTerm])

    const getListSearch = (overrides: Partial<Search> = {}): Search => {
        const nextSearch = { ...listSearch, ...overrides }

        return {
            ...nextSearch,
            searchTerm: nextSearch.searchTerm || undefined,
        }
    }

    const submitSearch = () => {
        const nextSearchTerm = search.trim()

        if (nextSearchTerm === submittedSearchTerm) {
            return
        }

        void navigate({
            to: '/product/community/categories/$categoryId',
            params: { categoryId },
            search: getListSearch({ page: 1, searchTerm: nextSearchTerm }),
        })
    }

    const createTopic = async (input: { title: string; message: string; attachments: AttachmentInput }) => {
        const topic = await client.product.community.topics.createTopic.mutate({
            categoryId,
            ...input,
        })
        await navigate({
            to: '/product/community/categories/$categoryId/topics/$topicId',
            params: { categoryId, topicId: topic._id.toString() },
            search: getListSearch(),
        })
    }

    return (
        <Page
            title={category?.title || t('product.community.category.loading')}
            description={category?.description}
            parentLink={{ to: '/product/community' }}
            descriptionClassName="max-w-none"
            className="flex min-h-full flex-col space-y-6 pb-8"
        >
            <Toolbar
                search={search}
                setSearch={setSearch}
                submitSearch={submitSearch}
                listSearch={listSearch}
                category={category}
                categoryId={categoryId}
                getListSearch={getListSearch}
                canCreateTopic={canCreateTopic}
                setTopicDialogOpen={setTopicDialogOpen}
            />
            <TopicList
                topics={topics}
                topicCount={topicCount}
                submittedSearchTerm={submittedSearchTerm}
                canCreateTopic={canCreateTopic}
                setTopicDialogOpen={setTopicDialogOpen}
                categoryId={categoryId}
                getListSearch={getListSearch}
            />
            <TopicDialog open={topicDialogOpen} onOpenChange={setTopicDialogOpen} onSubmit={createTopic} />
        </Page>
    )
})
